import { createContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { getUserCurrentUser } from '@/api/generated/user/user'
import { normalizeUserResponse } from '@/api/types'
import type { UserApiResponse } from '@/api/types'
import type { User } from '@/api/types'
import { getCookie } from '@/lib/cookies'
import { shouldPing, markPinged } from '@/lib/activityPing'
import { telemetry } from '@/telemetry'

interface UserContextValue {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  reload: () => Promise<void>
  setUser: (user: User | null) => void
}

export const UserContext = createContext<UserContextValue>({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  reload: async () => {},
  setUser: () => {},
})

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const isAuthenticated = user !== null

  const reload = useCallback(async () => {
    const token = getCookie('AccessToken')
    if (!token) {
      setUser(null)
      setIsLoading(false)
      return
    }
    try {
      const raw = await getUserCurrentUser()
      markPinged()
      setUser(normalizeUserResponse(raw as unknown as UserApiResponse))
    } catch {
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    reload()
  }, [reload])

  // Daily activity meter (client half). GET /User/CurrentUser is what makes the server
  // record a `userlogons` row, and this provider mounts once per FULL PAGE LOAD --
  // client-side navigation does not remount it. So a user who opens the site on Monday and
  // works out of that tab all week registers exactly one active day, which is what made
  // the old activeOwners count under-report the SPA so badly.
  //
  // Re-pinging when the tab is brought back into view closes that gap. A tab nobody has
  // looked at is deliberately NOT counted -- no interval, no background heartbeat --
  // because the thing being measured is whether the owner actually showed up.
  useEffect(() => {
    if (!isAuthenticated) return

    const ping = () => {
      if (document.visibilityState !== 'visible') return
      if (!shouldPing()) return
      markPinged()
      // Fire-and-forget: called ONLY for its server-side effect
      // (UserController.GetCurrentUser -> RecordSessionActivityAsync). Deliberately does
      // not setUser -- re-rendering every consumer on identical data would be pure churn.
      void getUserCurrentUser().catch(() => {})
    }

    document.addEventListener('visibilitychange', ping)
    window.addEventListener('focus', ping)
    return () => {
      document.removeEventListener('visibilitychange', ping)
      window.removeEventListener('focus', ping)
    }
  }, [isAuthenticated])

  // Tie the App Insights authenticated-user context to the resolved user.
  // Every auth path funnels through this state (initial session restore,
  // explicit login via reload/setUser, and clearing on logout/failure), so
  // this single effect covers set AND clear. Numeric id only — never
  // username/email (PII rule). No-op when telemetry isn't configured.
  useEffect(() => {
    if (user) telemetry.setAuthenticatedUser(user.id)
    else telemetry.clearAuthenticatedUser()
  }, [user])

  return (
    <UserContext.Provider value={{
      user,
      isLoading,
      isAuthenticated,
      reload,
      setUser,
    }}>
      {children}
    </UserContext.Provider>
  )
}
